import React, { useEffect, useState } from 'react';
import {
  PBX_BREAK_REASONS,
  BREAK_REASON_DIALOG_TITLE,
  BREAK_REASON_DIALOG_SUBTITLE,
  AGENT_STATUS_COLORS,
  AGENT_STATUS,
} from './pbxConstants';

const styles = {
  overlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: '10% / 5%',          // 👈 matches phone screen radius
    overflow: 'hidden',
    zIndex: 20,                        // above the control center (z 10)
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8%',
  },
  backdrop: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(10, 10, 14, 0.55)',
    backdropFilter: 'blur(14px) saturate(160%)',
    WebkitBackdropFilter: 'blur(14px) saturate(160%)',
  },
  sheet: {
    position: 'relative',
    width: '100%',
    background: 'rgba(58, 58, 70, 0.92)',
    backdropFilter: 'blur(30px) saturate(180%)',
    WebkitBackdropFilter: 'blur(30px) saturate(180%)',
    borderRadius: '22px',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    boxShadow: '0 18px 40px rgba(0, 0, 0, 0.45)',
    padding: '18px 14px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    animation: 'pbxSheetPop 0.22s cubic-bezier(0.25, 0.8, 0.3, 1)',
  },
  header: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  headerIcon: {
    width: '38px', height: '38px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: AGENT_STATUS_COLORS[AGENT_STATUS.BREAK],
    boxShadow: `0 0 14px ${AGENT_STATUS_COLORS[AGENT_STATUS.BREAK]}70`,
    marginBottom: '2px',
  },
  title: { fontSize: '16px', fontWeight: 700, color: '#fff' },
  subtitle: {
    fontSize: '11px', fontWeight: 500, color: 'rgba(255,255,255,0.65)',
    textAlign: 'center', lineHeight: 1.3,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    maxHeight: '190px',
    overflowY: 'auto',
    paddingRight: '2px',
  },
  emptyText: {
    fontSize: '12px', color: 'rgba(255,255,255,0.6)',
    textAlign: 'center', padding: '14px 6px', lineHeight: 1.4,
  },
  option: {
    display: 'flex', alignItems: 'center', gap: '10px',
    width: '100%', textAlign: 'left',
    padding: '10px 12px',
    borderRadius: '14px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.08)',
    cursor: 'pointer',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    transition: 'background 0.15s ease, border-color 0.15s ease',
  },
  optionSelected: {
    background: `${AGENT_STATUS_COLORS[AGENT_STATUS.BREAK]}33`,
    borderColor: AGENT_STATUS_COLORS[AGENT_STATUS.BREAK],
  },
  radio: {
    width: '17px', height: '17px', borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  radioSelected: {
    borderColor: AGENT_STATUS_COLORS[AGENT_STATUS.BREAK],
  },
  radioDot: {
    width: '9px', height: '9px', borderRadius: '50%',
    background: AGENT_STATUS_COLORS[AGENT_STATUS.BREAK],
  },
  optionLabel: { fontSize: '13px', fontWeight: 500, color: '#fff' },
  actions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '2px' },
  btn: {
    padding: '10px 8px',
    borderRadius: '14px',
    border: 'none',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    cursor: 'pointer',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  },
  cancelBtn: { background: 'rgba(255,255,255,0.14)', color: '#fff' },
  confirmBtn: { background: AGENT_STATUS_COLORS[AGENT_STATUS.BREAK], color: '#fff' },
  confirmBtnDisabled: {
    background: 'rgba(255,255,255,0.14)',
    color: 'rgba(255,255,255,0.4)',
    cursor: 'not-allowed',
  },
};

function PBXBreakReasonDialog({ isOpen, onCancel, onConfirm, reasons = PBX_BREAK_REASONS }) {
  const [selected, setSelected] = useState(null);

  // Reset the selection every time the dialog is reopened.
  useEffect(() => {
    if (isOpen) setSelected(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm(selected.value, selected.label);
  };

  return (
    <div
      style={styles.overlay}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      <style>{`
        @keyframes pbxSheetPop {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div style={styles.backdrop} onPointerDown={onCancel} />

      <div style={styles.sheet}>
        <div style={styles.header}>
          <div style={styles.headerIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          </div>
          <div style={styles.title}>{BREAK_REASON_DIALOG_TITLE}</div>
          <div style={styles.subtitle}>{BREAK_REASON_DIALOG_SUBTITLE}</div>
        </div>

        <div style={styles.list}>
          {reasons.length === 0 ? (
            <div style={styles.emptyText}>No break reasons configured.</div>
          ) : (
            reasons.map((reason) => {
              const isSelected = selected?.value === reason.value;
              return (
                <button
                  key={reason.value}
                  type="button"
                  style={{ ...styles.option, ...(isSelected ? styles.optionSelected : {}) }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelected(reason);
                  }}
                >
                  <div style={{ ...styles.radio, ...(isSelected ? styles.radioSelected : {}) }}>
                    {isSelected && <div style={styles.radioDot} />}
                  </div>
                  <span style={styles.optionLabel}>{reason.label}</span>
                </button>
              );
            })
          )}
        </div>

        <div style={styles.actions}>
          <button
            type="button"
            style={{ ...styles.btn, ...styles.cancelBtn }}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onCancel(); }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selected}
            style={{ ...styles.btn, ...(selected ? styles.confirmBtn : styles.confirmBtnDisabled) }}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); handleConfirm(); }}
          >
            Go On Break
          </button>
        </div>
      </div>
    </div>
  );
}

export default PBXBreakReasonDialog;
